import { memo, useEffect, useState } from 'react';
import styled from 'styled-components';
import { getDefaultStore, useAtom, useSetAtom } from 'jotai';
import { show_UploadTask_UploadPanel_Atom, uploadTaskPreloadedFilesAtom } from './store';
import { Button, Input, message, Modal, Select } from 'antd';
import Dragger from 'antd/es/upload/Dragger';
import { InboxOutlined, EyeOutlined, CloudUploadOutlined } from '@ant-design/icons';
import { remote_mapData_getByProject } from '~/common/typescript/apis/MapDataAPIs';
import { currentProjectAtom } from '~/stores/ProjectStore';
import { mapDataListAtom, showUploadTaskPanelAtom } from '../../stores/mapStore';
import { UploadTask_Create_Request, UploadTaskAPIs } from '~/common/typescript/apis/UploadTaskAPIs';
import FormItemWrapper from '~/components/common/FormItemWrapper/FormItemWrapper';
import { UploadTask_Metadata } from '~/common/typescript/models/UploadTask';
import dayjs from 'dayjs';
import ProjectionSelector, { ProjectionSelection } from '~/components/app_common/ProjectionSelector/ProjectionSelector';
import * as Turf from '@turf/turf';
import { activeUploadTasksCountAtom, uploadTaskListRefreshTrigger } from '~/stores/UploadTaskStore';

export const showPanel = (files?: File[]) => {
  const store = getDefaultStore();
  if (files && files.length > 0) {
    store.set(uploadTaskPreloadedFilesAtom, files);
  }
  store.set(show_UploadTask_UploadPanel_Atom, true);
};

export const hidePanel = () => {
  const store = getDefaultStore();
  store.set(show_UploadTask_UploadPanel_Atom, false);
};

const UploadTask_UploadPanel = () => {
  const [isOpen, setIsOpen] = useAtom(show_UploadTask_UploadPanel_Atom);
  const [preloadedFiles, setPreloadedFiles] = useAtom(uploadTaskPreloadedFilesAtom);
  const [currentProject] = useAtom(currentProjectAtom);
  const setMapDataLayers = useSetAtom(mapDataListAtom);
  const setActiveUploadTasksCount = useSetAtom(activeUploadTasksCountAtom);
  const setUploadTaskListRefreshTrigger = useSetAtom(uploadTaskListRefreshTrigger);
  const setShowUploadTaskPanel = useSetAtom(showUploadTaskPanelAtom);

  const [name, setName] = useState<string>(`DL ${dayjs().format('DD/MM/YYYY HH:mm')}`);
  const [projection, setProjection] = useState<ProjectionSelection>();

  const [showAdvanceOption, setShowAdvanceOption] = useState<boolean>(false);

  const [fileList, setFileList] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Sync preloaded files from drag & drop or external callers
  useEffect(() => {
    if (preloadedFiles.length > 0) {
      setFileList(preloadedFiles);
      if (preloadedFiles[0]) {
        const baseName = preloadedFiles[0].name.replace(/\.[^/.]+$/, '');
        setName(baseName);
      }
      setPreloadedFiles([]);
    }
  }, [preloadedFiles, isOpen]);

  const handleFileChange = (info: any) => {
    const { fileList } = info;
    const files = fileList.map((file: any) => file.originFileObj || file);
    setFileList(files);
    if (files.length > 0 && files[0].name) {
      const baseName = files[0].name.replace(/\.[^/.]+$/, '');
      setName(baseName);
      if (files[0].name.toLowerCase().endsWith('.dgn')) {
        setShowAdvanceOption(true);
      }
    }
  };

  const hasGeojson = fileList.some(
    (f) => f.name && (f.name.toLowerCase().endsWith('.geojson') || f.name.toLowerCase().endsWith('.json')),
  );

  const handleInstantPreview = async () => {
    const geojsonFile = fileList.find(
      (f) => f.name && (f.name.toLowerCase().endsWith('.geojson') || f.name.toLowerCase().endsWith('.json')),
    );
    if (!geojsonFile) return;

    const map = window.map;
    if (!map) {
      message.error('Bản đồ chưa sẵn sàng');
      return;
    }

    try {
      const text = await geojsonFile.text();
      const data = JSON.parse(text);

      if (!data.type) {
        message.error('Tệp không đúng định dạng GeoJSON chuẩn');
        return;
      }

      const sourceId = `client_preview_${Date.now()}`;
      const fillLayerId = `${sourceId}_fill`;
      const lineLayerId = `${sourceId}_line`;
      const circleLayerId = `${sourceId}_circle`;

      map.addSource(sourceId, {
        type: 'geojson',
        data: data,
      });

      // Layer Fill (Polygon)
      map.addLayer({
        id: fillLayerId,
        type: 'fill',
        source: sourceId,
        filter: ['any', ['==', '$type', 'Polygon'], ['==', '$type', 'MultiPolygon']],
        paint: {
          'fill-color': '#1677ff',
          'fill-opacity': 0.35,
        },
      });

      // Layer Line (LineString & Polygon borders)
      map.addLayer({
        id: lineLayerId,
        type: 'line',
        source: sourceId,
        filter: ['any', ['==', '$type', 'LineString'], ['==', '$type', 'MultiLineString'], ['==', '$type', 'Polygon'], ['==', '$type', 'MultiPolygon']],
        paint: {
          'line-color': '#0958d9',
          'line-width': 2,
        },
      });

      // Layer Circle (Point)
      map.addLayer({
        id: circleLayerId,
        type: 'circle',
        source: sourceId,
        filter: ['any', ['==', '$type', 'Point'], ['==', '$type', 'MultiPoint']],
        paint: {
          'circle-radius': 6,
          'circle-color': '#ff4d4f',
          'circle-stroke-color': '#ffffff',
          'circle-stroke-width': 1.5,
        },
      });

      // Zoom to layer bounds using turf
      const bbox = Turf.bbox(data);
      if (bbox && bbox.length >= 4 && !isNaN(bbox[0]) && isFinite(bbox[0])) {
        map.fitBounds(
          [
            [bbox[0], bbox[1]],
            [bbox[2], bbox[3]],
          ],
          { padding: 50, maxZoom: 18 },
        );
      }

      message.success(`Đã mở xem nhanh "${geojsonFile.name}" trên bản đồ!`);
      setIsOpen(false);
    } catch (err: any) {
      console.error('Instant preview error:', err);
      message.error('Không thể đọc file GeoJSON: ' + (err.message || 'Lỗi cú pháp'));
    }
  };

  const onClick_Upload = async () => {
    if (!name) {
      message.error('Hãy nhập tên hiển thị cho dữ liệu');
      return;
    }

    if (fileList.length === 0) {
      message.error('Hãy chọn hoặc kéo thả tệp vào đây');
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    try {
      const metadata: UploadTask_Metadata = {};
      if (projection) {
        metadata.force_srs = projection.srs;
        metadata.force_srs_name = projection.srs_name;
      }

      const createRequest: UploadTask_Create_Request = {
        title: name,
        project_id: currentProject?.id,
        files: fileList,
        metadata: metadata,
      };

      await UploadTaskAPIs.remote_createUploadTask(createRequest, (progressEvent: any) => {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / (progressEvent.total || 100));
        setUploadProgress(percentCompleted);
      });

      message.success('Đã tải lên tệp thành công! Đang xử lý tạo lát gạch bản đồ...');

      // Tự động mở panel tiến trình tải lên để người dùng theo dõi
      setShowUploadTaskPanel(true);

      // Trigger immediate watcher check & bottom-right loading indicator
      setActiveUploadTasksCount((prev) => Math.max(prev + 1, 1));
      setUploadTaskListRefreshTrigger((prev) => prev + 1);

      // Reset form and close modal on success
      setName('');
      setProjection(undefined);
      setFileList([]);
      setUploadProgress(0);
      setIsOpen(false);

      // Refresh the map data list if project ID is available
      if (currentProject?.id) {
        const mapDataList = await remote_mapData_getByProject(currentProject.id);
        setMapDataLayers((prev) => {
          return (mapDataList || []).filter(Boolean).map((newLayer: any) => {
            const existing = prev.find((l) => l?.id === newLayer?.id);
            return {
              ...newLayer,
              visible: existing?.visible ?? false,
            };
          });
        });
      }
    } catch (error) {
      console.error('Upload failed:', error);
      message.error('Tải lên thất bại');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Modal
      title="Tải lên dữ liệu GIS"
      open={isOpen}
      onCancel={() => setIsOpen(false)}
      footer={null}
      destroyOnClose
    >
      <UI_Wrapper>
        <Dragger
          multiple={true}
          fileList={fileList as any}
          onChange={handleFileChange}
          beforeUpload={() => false}
        >
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">Vui lòng chọn hoặc kéo thả tệp vào đây để tải lên</p>
          <p className="ant-upload-hint">
            Hỗ trợ MicroStation DGN (.dgn v7/v8), Shapefile (nén ZIP hoặc tải đủ .shp, .shx, .dbf, .prj), GeoJSON, KML, GeoTIFF, MapInfo...
          </p>
        </Dragger>

        <div style={{ marginTop: 16 }}>
          <FormItemWrapper label="Tên hiển thị" labelWidth={200}>
            <Input
              placeholder="Tên hiển thị"
              style={{ marginBottom: 8 }}
              value={name}
              onChange={(e) => {
                setName(e.target.value);
              }}
            />
          </FormItemWrapper>

          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              type="link"
              style={{ fontSize: 12 }}
              onClick={() => setShowAdvanceOption(!showAdvanceOption)}
              size="small"
            >
              {showAdvanceOption ? 'Ẩn' : 'Hiển thị'} tuỳ chọn nâng cao
            </Button>
          </div>

          {showAdvanceOption && (
            <>
              <FormItemWrapper label="Hệ chiếu (Không bắt buộc)" labelWidth={200}>
                <div style={{ marginBottom: 8 }}>
                  <ProjectionSelector defaultValue={projection} onChange={(value) => setProjection(value)} />
                </div>
              </FormItemWrapper>

              <FormItemWrapper label="Bảng mã (Không bắt buộc)" labelWidth={200}>
                <Select placeholder="Bảng mã (Mặc định UTF-8)" style={{ width: '100%', marginBottom: 8 }} />
              </FormItemWrapper>
            </>
          )}
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          {hasGeojson && (
            <Button icon={<EyeOutlined />} onClick={handleInstantPreview}>
              Xem nhanh trên bản đồ
            </Button>
          )}
          <Button
            type="primary"
            icon={<CloudUploadOutlined />}
            loading={isUploading}
            onClick={onClick_Upload}
          >
            {isUploading ? `Đang tải lên (${uploadProgress}%)` : 'Tải lên đám mây'}
          </Button>
        </div>
      </UI_Wrapper>
    </Modal>
  );
};

export default memo(UploadTask_UploadPanel);

const UI_Wrapper = styled.div``;
